import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { colors } from "../constants/colors";
import newChatIcon from "../../../../assets/icons/newChat.svg";
import moreIcon from "../../../../assets/icons/more.svg";
import settingsIcon from "../../../../assets/icons/regular.svg";
import infoIcon from "../../../../assets/icons/info.svg";
import arrowUpIcon from "../../../../assets/icons/arrowUp.svg";
import addIcon from "../../../../assets/icons/add.svg";
import { useCurrentUser } from "../store/authStore";
import { useToastStore } from "../store/toastStore";
// import { useAppointmentStore } from "../store/AppointmentStore";
import { sendAgentsMessage, startNewChat, resetConversation } from "../services/agent/agentsClient";
import TypingIndicator from "../components/ui/TypingIndicator";
import ConnectionManagerCard from "../components/ui/ConnectionManagerCard";
import ListResourcePrompt from "../components/ui/ListResourcePrompt";
import { promptChat } from "../constants";
import { Loader2, Edit, Copy, Bookmark, Link, RotateCcw, ThumbsUp, ThumbsDown, Volume2 } from "lucide-react";
import { handleCopyTextToClipboard } from "../utils/clipboard";
import { useAgentStore } from "../store/agentStore";
import { parseLooseJson } from "../utils/json";

interface ChatMessage {
  id: string;
  content: string | any; // Allow any for JSON responses
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  isNullResult?: boolean;
}

// Unique ID generator to prevent duplicate keys
let messageIdCounter = 0;
const generateUniqueId = () => {
  messageIdCounter += 1;
  return `${Date.now()}-${messageIdCounter}`;
};

import AboutModal from "../components/ui/AboutModal";
import ResourceDetailPanel from "../components/ui/ResourceDetailPanel";
import { getDetailsResource } from "../services/resource/getDetailsResource";

// Helper function to transform string response format to resource_list format
const transformStringResponseToResourceList = (rawResponse: any) => {
  let parsedResponse = rawResponse;

  // First, check if this is a Copilot Studio response with text field containing JSON
  if (rawResponse && typeof rawResponse === "object" && rawResponse.type === "message" && rawResponse.text) {
    console.log("🔍 Detected Copilot Studio response, parsing text field...");
    try {
      // Parse the JSON string in the text field
      const textParsed = parseLooseJson(rawResponse.text) ?? JSON.parse(rawResponse.text);
      if (textParsed && typeof textParsed === "object") {
        parsedResponse = textParsed;
        // console.log("✅ Successfully parsed text field:", parsedResponse);
      }
    } catch (e) {
      console.log("❌ Failed to parse text field as JSON:", e);
      // Keep original response if parsing fails
      return rawResponse;
    }
  }

  // Now check if the parsed content needs transformation to resource_list format
  if (
    parsedResponse &&
    typeof parsedResponse === "object" &&
    parsedResponse.data &&
    parsedResponse.data.resources &&
    !parsedResponse.type
  ) {
    // console.log("🔄 Transforming string response format to resource_list format");

    const transformedResponse = {
      type: "resource_list",
      data: {
        resources: parsedResponse.data.resources,
        totalCount: parsedResponse.data.totalCount || parsedResponse.data.pagination?.totalCount || 0,
        totalPage:
          parsedResponse.data.pagination?.totalPages ||
          Math.ceil(
            (parsedResponse.data.totalCount || parsedResponse.data.pagination?.totalCount || 0) /
              (parsedResponse.data.pagination?.pageSize || 10)
          ),
        currentPage: parsedResponse.data.pagination?.currentPage || 1,
        hasNext: parsedResponse.data.pagination?.hasNext || false,
        hasPrevious: parsedResponse.data.pagination?.hasPrevious || false,
        totalResources: parsedResponse.data.totalCount || parsedResponse.data.pagination?.totalCount || 0,
        success: parsedResponse.data.success !== false,
        errorMessage: parsedResponse.data.errorMessage || null,
        reply: parsedResponse.message || "Available resources retrieved successfully",
        parsedCriteria: {
          resourceType: null,
          minCapacity: null,
          maxCapacity: null,
          isAvailable: null,
          startDate: null,
          endDate: null,
          originalQuery: "",
          location: null,
          locationPaths: [],
          categories: [],
          categoryIds: [],
          requiredItems: [],
          requiredItemIds: [],
          requiredPropertyTypes: [],
          requiredPropertyTypeIds: [],
          requiredAmenities: [],
          pageNumber: parsedResponse.data.pagination?.currentPage || 1,
          pageSize: parsedResponse.data.pagination?.pageSize || 10,
          includeTotalCount: true,
          useExactMatching: false,
          includeRelatedData: false,
          hasSpecificCriteria: false,
          usesOptimizedSearch: false,
        },
      },
    };

    console.log("✅ Transformed response:", transformedResponse);
    return transformedResponse;
  }

  return parsedResponse;
};

const Agent = () => {
  const currentUser = useCurrentUser();
  const { showToast } = useToastStore();
  const { setPagingContext, clearPagingContext } = useAgentStore();
  // const { initializeAppointmentTime, startTime, endTime } = useAppointmentStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isStartChat, setIsStartChat] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastUserMessageForRetry, setLastUserMessageForRetry] = useState<string>("");
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  
  // Resource detail modal state
  const [selectedResourceForDetail, setSelectedResourceForDetail] = useState<any>(null);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [loadingResourceId, setLoadingResourceId] = useState<string | null>(null);
  
  const lastMessageRef = useRef<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const handleViewResourceDetailRef = useRef<((resource: any) => void) | null>(null);

  // Memoize computed values
  const canSendMessage = useMemo(() => {
    return inputMessage.trim() && !isLoading;
  }, [inputMessage, isLoading]);

  const greeting = useMemo(() => {
    return `Hi ${currentUser?.name}. How can I help you today?`;
  }, [currentUser?.name]);

  // Track if user can scroll down more
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Track hovered message for showing actions
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);

  // Track current prompt suggestions
  const [currentPromptSuggestions, setCurrentPromptSuggestions] = useState<string[]>([]);

  // Auto scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    // Only scroll if we have messages and chat has started
    if (messages.length > 0 && isStartChat) {
      const lastMessage = messages[messages.length - 1];

      // Check if the last message is interactive content (resource list, cards, etc.)
      const isInteractiveContent =
        lastMessage &&
        !lastMessage.isUser &&
        !lastMessage.isTyping &&
        ((typeof lastMessage.content === "object" &&
          lastMessage.content !== null &&
          (lastMessage.content?.type === "resource_list" ||
            lastMessage.content?.type === "message" ||
            lastMessage.content?.attachments ||
            lastMessage.content?.data?.resources ||
            Array.isArray(lastMessage.content?.data?.resources))) ||
          (typeof lastMessage.content === "string" &&
            (lastMessage.content.includes("resource_list") ||
              lastMessage.content.includes("attachments") ||
              lastMessage.content.includes('"type":"resource_list"') ||
              lastMessage.content.includes('"resources":['))));

      console.log("Scroll logic - isInteractiveContent:", isInteractiveContent, "lastMessage:", lastMessage);

      if (isInteractiveContent) {
        // For interactive content, don't auto-scroll at all - let user see it naturally
        console.log("🚫 Skipping auto-scroll for interactive content");
        // Just show scroll hint if needed
        setTimeout(() => {
          const container = messagesEndRef.current?.parentElement;
          if (container) {
            const maxScroll = container.scrollHeight - container.clientHeight;
            const currentScroll = container.scrollTop;
            setShowScrollHint(currentScroll < maxScroll - 100);
          }
        }, 100);
      } else if (!lastMessage?.isTyping) {
        // For regular text messages, scroll to bottom normally
        console.log("⬇️ Auto-scrolling for regular text message");
        setTimeout(() => scrollToBottom(), 200);
      }
    }
  }, [messages, isStartChat, scrollToBottom]);

  // Focus input when component mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle scroll events to hide scroll hint when user scrolls to bottom
  useEffect(() => {
    const chatContainer = messagesEndRef.current?.parentElement;
    if (!chatContainer) return;

    const handleScroll = () => {
      const maxScroll = chatContainer.scrollHeight - chatContainer.clientHeight;
      const currentScroll = chatContainer.scrollTop;

      // Hide scroll hint if user is near the bottom
      if (currentScroll >= maxScroll - 50) {
        setShowScrollHint(false);
      }
    };

    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, [isStartChat]);

  // 🔥 NEW: Extract suggestions when messages change
  useEffect(() => {
    if (!messages.length) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isUser || lastMessage?.isTyping) return;

    const extractSuggestions = (content: any) => {
      let suggestions: string[] = [];

      // Direct object with resource_list type
      if (content?.type === "resource_list" && content?.data?.promptSuggestion) {
        suggestions = content.data.promptSuggestion;
        console.log("🔥 Found direct resource_list suggestions:", suggestions);
      }
      // Check text field that might contain JSON
      else if (content?.text) {
        try {
          const parsed = parseLooseJson(content.text) ?? JSON.parse(content.text);
          if (parsed?.type === "resource_list" && parsed?.data?.promptSuggestion) {
            suggestions = parsed.data.promptSuggestion;
            console.log("🔥 Found parsed text suggestions:", suggestions);
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }

      return suggestions;
    };

    const suggestions = extractSuggestions(lastMessage.content);

    if (Array.isArray(suggestions) && suggestions.length > 0) {
      console.log("🔥 useEffect: Setting suggestions from latest message:", suggestions);
      setCurrentPromptSuggestions(suggestions);
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 72) + "px";
    }
  }, [inputMessage]);

  // useEffect(() => {
  //   initializeAppointmentTime();
  // }, []);

  const addMessage = useCallback((content: string | any, isUser: boolean, isTyping = false, isNullResult = false) => {
    const newMessage: ChatMessage = {
      id: generateUniqueId(),
      content,
      isUser,
      timestamp: new Date(),
      isTyping,
      isNullResult,
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  // Capture and persist paging context if present in agent response
  const capturePagingFromResponse = useCallback(
    (parsedResponse: any, lastQuery: string) => {
      try {
        const content = typeof parsedResponse === "object" ? parsedResponse : undefined;
        const data =
          content?.type === "resource_list"
            ? content?.data
            : content?.text
            ? (parseLooseJson(content.text) ?? JSON.parse(content.text))?.data
            : undefined;
        if (data && (data.hasNext !== undefined || data.hasPrevious !== undefined)) {
          setPagingContext({
            currentPage: data.currentPage ?? null,
            totalPage: data.totalPage ?? null,
            hasNext: data.hasNext ?? null,
            hasPrevious: data.hasPrevious ?? null,
            pageSize: data.pageSize ?? null,
            infoPagination: data.infoPagination ?? null,
            filters: data.filters ?? null,
            sessionId: data.sessionId ?? null,
            lastQuery,
          });
        }
      } catch (_) {}
    },
    [setPagingContext]
  );

  const updateLastMessage = useCallback((content: string | any, isTyping = false, isNullResult = false) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && !lastMessage.isUser) {
        lastMessage.content = content;
        lastMessage.isTyping = isTyping;
        lastMessage.isNullResult = isNullResult;
      }
      return newMessages;
    });
  }, []);

  const handleChooseDefaultQuestion = useCallback((questionText: string) => {
    setInputMessage(questionText);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleSendMessage = useCallback(
    async (customMessage?: string) => {
      const messageToSend = customMessage || inputMessage.trim();
      if (!messageToSend || isLoading) return;

      console.log("handleSendMessage called with:", messageToSend);

      // Immediately update UI state for better UX
      setIsLoading(true);
      setLastUserMessageForRetry(messageToSend);
      lastMessageRef.current = messageToSend;

      // Clear input immediately
      if (!customMessage) {
        setInputMessage("");
      }

      // Clear current suggestions when sending new message
      setCurrentPromptSuggestions([]);

      // 🔥 RESET PAGING CONTEXT for new user queries (not pagination actions)
      // This ensures that when user asks a new question, pagination starts fresh
      clearPagingContext();

      // Batch state updates to reduce re-renders
      setMessages((prev) => [
        ...prev,
        {
          id: generateUniqueId(),
          content: messageToSend,
          isUser: true,
          timestamp: new Date(),
        },
        {
          id: generateUniqueId(),
          content: "",
          isUser: false,
          timestamp: new Date(),
          isTyping: true,
        },
      ]);

      // Start chat in parallel if needed
      const chatPromise = !isStartChat ? startNewChat() : Promise.resolve();
      if (!isStartChat) {
        setIsStartChat(true);
      }

      try {
        // Send message and get response
        const rawResponse = await sendAgentsMessage(
          messageToSend,
          currentUser?.email
          // startTime,
          // endTime
        );

        // Handle response object from Copilot Studio and transform if needed
        let parsedResponse: any = transformStringResponseToResourceList(rawResponse);

        // Extract prompt suggestions from response
        console.log("🔍 Full parsedResponse:", parsedResponse);
        console.log("🔍 JSON stringify parsedResponse:", JSON.stringify(parsedResponse, null, 2));
        console.log("🔍 Response type:", typeof parsedResponse);
        console.log(
          "🔍 Response keys:",
          parsedResponse && typeof parsedResponse === "object" ? Object.keys(parsedResponse) : "N/A"
        );
        let suggestions: string[] = [];

        if (parsedResponse && typeof parsedResponse === "object") {
          // Check data.promptSuggestion
          console.log("🔍 Checking data.promptSuggestion:", parsedResponse?.data?.promptSuggestion);

          // Check direct promptSuggestion
          console.log("🔍 Checking promptSuggestion:", parsedResponse?.promptSuggestion);

          // Check text field
          console.log("🔍 Checking text field:", parsedResponse?.text);

          // Try multiple possible locations for suggestions
          suggestions =
            parsedResponse?.data?.promptSuggestion ||
            parsedResponse?.promptSuggestion ||
            parsedResponse?.text?.data?.promptSuggestion ||
            parsedResponse?.data?.suggestions ||
            parsedResponse?.suggestions ||
            parsedResponse?.data?.prompts ||
            parsedResponse?.prompts ||
            [];

          // If text field contains JSON, try to parse it
          if (!suggestions.length && parsedResponse?.text && typeof parsedResponse.text === "string") {
            console.log("🔍 Trying to parse text field as JSON...");
            console.log("🔍 Text field content:", parsedResponse.text);
            try {
              const textParsed = parseLooseJson(parsedResponse.text) ?? JSON.parse(parsedResponse.text);
              console.log("🔍 Parsed text content:", textParsed);
              console.log("🔍 Text parsed type:", textParsed?.type);
              console.log("🔍 Text parsed data:", textParsed?.data);
              console.log("🔍 Text parsed data.promptSuggestion:", textParsed?.data?.promptSuggestion);
              console.log("🔍 Text parsed promptSuggestion:", textParsed?.promptSuggestion);

              // If the parsed content is the resource_list format, extract suggestions
              if (textParsed?.type === "resource_list" && textParsed?.data?.promptSuggestion) {
                suggestions = textParsed.data.promptSuggestion;
                console.log("🎯 Found resource_list format with suggestions:", suggestions);
              } else {
                suggestions =
                  textParsed?.data?.promptSuggestion ||
                  textParsed?.promptSuggestion ||
                  textParsed?.data?.suggestions ||
                  textParsed?.suggestions ||
                  textParsed?.data?.prompts ||
                  textParsed?.prompts ||
                  [];
              }
            } catch (e) {
              console.log("❌ Failed to parse text field as JSON:", e);
            }
          }

          console.log("🔍 Final extracted suggestions:", suggestions);
          console.log("🔍 Is suggestions array?", Array.isArray(suggestions));
          console.log("🔍 Suggestions length:", suggestions?.length);

          if (Array.isArray(suggestions) && suggestions.length > 0) {
            console.log("✅ Setting suggestions:", suggestions);
            setCurrentPromptSuggestions(suggestions);
          } else {
            console.log("❌ No valid suggestions found");
            setCurrentPromptSuggestions([]);
          }
        } else {
          console.log("❌ parsedResponse is not an object");
          setCurrentPromptSuggestions([]);
        }

        // Persist paging context if available
        capturePagingFromResponse(parsedResponse, messageToSend);

        // Remove typing indicator and add AI response
        setMessages((prev) => {
          const newMessages = prev.filter((msg) => !msg.isTyping);
          return [
            ...newMessages,
            {
              id: generateUniqueId(),
              content: parsedResponse,
              isUser: false,
              timestamp: new Date(),
            },
          ];
        });
      } catch (error) {
        console.error("Error sending message:", error);
        // Clear suggestions on error
        setCurrentPromptSuggestions([]);
        // Remove typing indicator and show error
        setMessages((prev) => {
          const newMessages = prev.filter((msg) => !msg.isTyping);
          return [
            ...newMessages,
            {
              id: generateUniqueId(),
              content: "Sorry, something went wrong. Please try again.",
              isUser: false,
              timestamp: new Date(),
            },
          ];
        });
      } finally {
        setIsLoading(false);
        setIsRetrying(false);
        inputRef?.current?.focus();
      }
    },
    [inputMessage, isLoading, currentUser?.email, currentUser?.name, isStartChat, clearPagingContext]
  );

  // Handle clicking on prompt suggestions
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      // Clear suggestions immediately when clicked
      setCurrentPromptSuggestions([]);
      // Don't set to input, just send directly
      handleSendMessage(suggestion);
    },
    [handleSendMessage]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
      // Allow Shift+Enter to create new line
      if (e.key === "Enter" && e.shiftKey) {
        // Don't prevent default, let it create new line
        return;
      }
    },
    [handleSendMessage]
  );

  const handleRetryMessage = async (messageToRetry: string) => {
    console.log("handleRetryMessage called with:", messageToRetry);

    if (!messageToRetry || isLoading) return;

    setIsLoading(true);
    setIsRetrying(true);

    // 🔥 RESET PAGING CONTEXT for retry (this is a new query, not pagination)
    clearPagingContext();

    // Add typing indicator for AI
    addMessage("", false, true);

    try {
      // Send message and get response
      const rawResponse = await sendAgentsMessage(
        messageToRetry,
        currentUser?.email
        // startTime,
        // endTime
      );

      // Handle response object from Copilot Studio
      let parsedResponse: any = rawResponse;

      // If response is an object (from Copilot Studio), use it directly
      if (typeof rawResponse === "object" && rawResponse !== null) {
        parsedResponse = rawResponse;
      } else if (typeof rawResponse === "string") {
        // Try to parse JSON from string
        try {
          const tmp = parseLooseJson(rawResponse) ?? JSON.parse(rawResponse);
          if (typeof tmp === "object" && tmp !== null) {
            parsedResponse = tmp;
          }
        } catch (_) {
          // not JSON, keep as string
        }
      }

      // Persist paging context if available
      capturePagingFromResponse(parsedResponse, messageToRetry);

      // Remove typing indicator and add AI response
      setMessages((prev) => {
        const newMessages = prev.filter((msg) => !msg.isTyping);
        return [
          ...newMessages,
          {
            id: generateUniqueId(),
            content: parsedResponse,
            isUser: false,
            timestamp: new Date(),
          },
        ];
      });
    } catch (error) {
      console.error("Error retrying message:", error);
      // Remove typing indicator and show error
      setMessages((prev) => {
        const newMessages = prev.filter((msg) => !msg.isTyping);
        return [
          ...newMessages,
          {
            id: generateUniqueId(),
            content: "Sorry, something went wrong. Please try again.",
            isUser: false,
            timestamp: new Date(),
          },
        ];
      });
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  const handleNewChat = useCallback(async () => {
    setMessages([]);
    setConversationId(null);
    setIsStartChat(false);
    setLastUserMessageForRetry("");
    lastMessageRef.current = "";
    setIsRetrying(false);
    setCurrentPromptSuggestions([]);
    // Clear paging context on new conversation
    clearPagingContext();
    try {
      // ensure module-level conversation id is cleared
      resetConversation();
      const newConversationId = await startNewChat();
      setConversationId(newConversationId);
    } catch (error) {
      console.error("Error starting new chat:", error);
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }, [clearPagingContext]);

  // Message action handlers
  const handleEditMessage = useCallback((messageId: string, content: string) => {
    console.log("Edit message:", messageId, content);
    // Set the message content to input for editing
    setInputMessage(content);
    inputRef.current?.focus();
  }, []);

  const handleCopyMessage = useCallback(
    async (content: string) => {
      try {
        await handleCopyTextToClipboard(content);
        showToast("Message copied to clipboard!", true);
      } catch (error) {
        console.error("Failed to copy message:", error);
        showToast("Failed to copy message", false);
      }
    },
    [showToast]
  );

  const handleBookmarkMessage = useCallback((messageId: string, content: string) => {
    console.log("Bookmark message:", messageId, content);
    // Add bookmark logic here
  }, []);

  const handleShareMessage = useCallback((messageId: string, content: string) => {
    console.log("Share message:", messageId, content);
    // Add share logic here
  }, []);

  // Agent message action handlers
  const handleRetryAgentMessage = useCallback(
    (messageId: string) => {
      console.log("Retry agent message:", messageId);
      // Resend the last user message that triggered this response
      const messageToRetry = lastMessageRef.current || lastUserMessageForRetry;
      if (messageToRetry && !isRetrying) {
        handleRetryMessage(messageToRetry);
      }
    },
    [lastUserMessageForRetry, isRetrying, handleRetryMessage]
  );

  const handleThumbsUp = useCallback(
    (messageId: string) => {
      console.log("Thumbs up for message:", messageId);
      showToast("Feedback recorded: 👍", true);
      // Add feedback logic here
    },
    [showToast]
  );

  const handleThumbsDown = useCallback(
    (messageId: string) => {
      console.log("Thumbs down for message:", messageId);
      showToast("Feedback recorded: 👎", true);
      // Add feedback logic here
    },
    [showToast]
  );

  const handleSpeakMessage = useCallback(
    (content: string) => {
      console.log("Speak message:", content);
      // Add text-to-speech logic here
      if ("speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance(content);
        utterance.lang = "en-US";
        speechSynthesis.speak(utterance);
        showToast("Speaking message...", true);
      } else {
        showToast("Speech synthesis not supported", false);
      }
    },
    [showToast]
  );

  const handleCopyAgentMessage = useCallback(
    async (content: string) => {
      try {
        // Extract text content from complex objects
        let textContent = content;
        if (typeof content === "object") {
          textContent = JSON.stringify(content, null, 2);
        }
        await handleCopyTextToClipboard(textContent);
        showToast("Response copied to clipboard!", true);
      } catch (error) {
        console.error("Failed to copy response:", error);
        showToast("Failed to copy response", false);
      }
    },
    [showToast]
  );

  const handleRenderMessage = useCallback((message: ChatMessage) => {
    if (message.isTyping) {
      return <TypingIndicator />;
    }

    if (message.isNullResult) {
      return <p>I'm sorry, I didn't quite understand what you meant.</p>;
    }

    // Handle JSON response
    if (typeof message.content === "string") {
      // Try to parse JSON from string
      try {
        const jsonData = parseLooseJson(message.content) ?? JSON.parse(message.content);
        return renderMessageContent(jsonData);
      } catch (error) {
        // Not JSON, display as normal text
        return <p className="whitespace-pre-wrap break-words">{message.content}</p>;
      }
    } else if (typeof message.content === "object" && message.content !== null) {
      // Handle object data (already parsed JSON)
      return renderMessageContent(message.content);
    } else {
      return <p className="whitespace-pre-wrap break-words">{String(message.content)}</p>;
    }
  }, []);

  // Paging handlers passed to list
  const handlePaging = useCallback(
    async (action: "next" | "previous") => {
      // Show typing indicator
      addMessage("", false, true);
      try {
        const rawResponse = await sendAgentsMessage(
          action === "next" ? "Load more" : "Load previous",
          currentUser?.email,
          undefined,
          undefined,
          { pagingAction: action }
        );

        // Handle response object from Copilot Studio and transform if needed (same as in handleSendMessage)
        let parsedResponse: any = transformStringResponseToResourceList(rawResponse);

        // Persist paging context if available
        capturePagingFromResponse(parsedResponse, action === "next" ? "Load more" : "Load previous");

        // Replace typing with response
        setMessages((prev) => {
          const newMessages = prev.filter((msg) => !msg.isTyping);
          return [
            ...newMessages,
            {
              id: generateUniqueId(),
              content: parsedResponse,
              isUser: false,
              timestamp: new Date(),
            },
          ];
        });
      } catch (error) {
        console.error("Error during paging:", error);
        setMessages((prev) => {
          const newMessages = prev.filter((msg) => !msg.isTyping);
          return [
            ...newMessages,
            {
              id: generateUniqueId(),
              content: "Failed to load page. Please try again.",
              isUser: false,
              timestamp: new Date(),
            },
          ];
        });
      }
    },
    [addMessage, currentUser?.email, capturePagingFromResponse]
  );

  const renderMessageContent = (jsonData: any) => {
    // Check for resource_list type (direct JSON response)
    if (jsonData.type === "resource_list" && jsonData.data?.resources) {
      // 🔥 REMOVED: Don't set state in render function to prevent infinite re-renders
      // The useEffect will handle extracting suggestions from messages
      return (
        <ListResourcePrompt
          data={jsonData.data}
          onLoadMore={() => handlePaging("next")}
          onLoadPrevious={() => handlePaging("previous")}
          onViewResourceDetail={handleViewResourceDetailRef.current || handleViewResourceDetail}
          loadingResourceId={loadingResourceId}
        />
      );
    }

    // Check for message type with attachments (connection manager)
    if (jsonData.type === "message" && jsonData.attachments && jsonData.attachments.length > 0) {
      const attachment = jsonData.attachments[0];
      if (attachment.contentType === "application/vnd.microsoft.card.adaptive" && attachment.content) {
        return (
          <div className="space-y-3">
            <ConnectionManagerCard
              content={attachment.content}
              onRetry={() => {
                // Handle retry action - resend the last user message
                const messageToRetry = lastMessageRef.current || lastUserMessageForRetry;
                console.log("Retry requested. Last user message (state):", lastUserMessageForRetry);
                console.log("Retry requested. Last user message (ref):", lastMessageRef.current);
                console.log("Message to retry:", messageToRetry);
                console.log("isRetrying:", isRetrying);

                if (messageToRetry && !isRetrying) {
                  console.log("Retrying with message:", messageToRetry);
                  // Remove only the connection manager message
                  setMessages((prev) =>
                    prev.filter((msg) => {
                      // Keep all messages except the one with connection manager card
                      if (msg.content?.type === "message" && msg.content?.attachments?.length > 0) {
                        const attachment = msg.content.attachments[0];
                        return !(attachment.contentType === "application/vnd.microsoft.card.adaptive");
                      }
                      return true;
                    })
                  );
                  // Then send the message again using dedicated retry function
                  handleRetryMessage(messageToRetry);
                } else {
                  console.log("No message to retry or already retrying");
                  console.log("messageToRetry:", messageToRetry);
                  console.log("isRetrying:", isRetrying);
                }
              }}
              onCancel={() => {
                // Handle cancel action - remove only the connection manager message
                console.log("Canceling connection manager message");
                setMessages((prev) =>
                  prev.filter((msg) => {
                    // Keep all messages except the one with connection manager card
                    if (msg.content?.type === "message" && msg.content?.attachments?.length > 0) {
                      const attachment = msg.content.attachments[0];
                      return !(attachment.contentType === "application/vnd.microsoft.card.adaptive");
                    }
                    return true;
                  })
                );
              }}
            />
          </div>
        );
      }
    }

    // Check if it's a Copilot Studio Activity object with text
    if (jsonData.text) {
      // Try to parse the text content as JSON first
      try {
        const parsedText = parseLooseJson(jsonData.text) ?? JSON.parse(jsonData.text);
        // If it's a resource_list, render it properly
        if (parsedText.type === "resource_list" && parsedText.data?.resources) {
          // 🔥 REMOVED: Don't set state in render function to prevent infinite re-renders
          // The useEffect will handle extracting suggestions from messages
          console.log("🔍 Agent.tsx: Rendering ListResourcePrompt (parsed text) with handleViewResourceDetail:", handleViewResourceDetail);
          return (
            <ListResourcePrompt
              data={parsedText.data}
              onLoadMore={() => handlePaging("next")}
              onLoadPrevious={() => handlePaging("previous")}
              onViewResourceDetail={handleViewResourceDetailRef.current || handleViewResourceDetail}
              loadingResourceId={loadingResourceId}
            />
          );
        }
        // If it's other JSON, display as formatted text
        return (
          <pre className="whitespace-pre-wrap break-words text-sm leading-[20px] font-[400] text-darkGray dark:text-softGray">
            {JSON.stringify(parsedText, null, 2)}
          </pre>
        );
      } catch (error) {
        // Not JSON, display as normal text
        return (
          <p className="whitespace-pre-wrap break-words text-sm leading-[20px] font-[400] text-darkGray dark:text-softGray">
            {jsonData.text}
          </p>
        );
      }
    }

    // If it's JSON but not a recognized type, display as formatted text
    return (
      <pre className="whitespace-pre-wrap break-words text-sm leading-[20px] font-[400] text-darkGray dark:text-softGray">
        {JSON.stringify(jsonData, null, 2)}
      </pre>
    );
  };

  const handleOpenAboutModal = () => {
    setIsAboutModalOpen(true);
  };

  const handleCloseAboutModal = () => {
    setIsAboutModalOpen(false);
  };

  // Resource detail modal handlers
  const handleViewResourceDetail = useCallback(async (resource: any) => {
    console.log("🚀 Agent.tsx: handleViewResourceDetail called with:", resource);
    
    // Early validation - ensure resource has minimum required data
    if (!resource) {
      console.warn("⚠️ handleViewResourceDetail: No resource provided");
      return;
    }

    // Get resource identifier for loading state
    const resourceIdentifier = resource?.id ?? resource?.ID ?? resource?.email ?? resource?.Email ?? resource?.name ?? resource?.Name;
    
    // Prevent multiple concurrent requests for the same resource
    if (loadingResourceId === resourceIdentifier) {
      console.log("🔄 Already loading this resource, ignoring request");
      return;
    }

    try {
      // Set loading state for this specific resource
      setLoadingResourceId(resourceIdentifier);

      // Get ID with better validation - 0 is a valid ID
      const id = resource?.id ?? resource?.ID;
      
      // If no ID available, show basic resource info
      if (id === null || id === undefined) {
        console.log("📄 No ID found, showing basic resource info:", resource.name || resource.Name);
        setSelectedResourceForDetail(resource);
        setIsResourceModalOpen(true);
        return;
      }

      // Convert to number and validate
      const numericId = Number(id);
      if (isNaN(numericId)) {
        console.warn("⚠️ Invalid ID format:", id);
        setSelectedResourceForDetail(resource);
        setIsResourceModalOpen(true);
        return;
      }

      console.log("🔍 Fetching details for resource ID:", numericId);
      
      // Show modal immediately with loading state
      setSelectedResourceForDetail({
        ...resource,
        _isLoadingDetails: true
      });
      setIsResourceModalOpen(true);
      
      // Fetch detailed information
      const details = await getDetailsResource(numericId);
      console.log("✅ Resource details loaded:", details);

      // Merge original resource with fetched details
      const mergedResource = {
        ...resource,
        ...details,
        // Ensure we keep important display fields from original resource
        displayName: resource.name || resource.Name || details?.name || details?.Name,
        displayEmail: resource.email || resource.Email || details?.email || details?.Email,
        _isLoadingDetails: false
      };

      setSelectedResourceForDetail(mergedResource);

    } catch (error) {
      console.error("❌ Failed to load resource details:", error);
      
      // Still show modal with basic resource info on API failure
      const fallbackResource = {
        ...resource,
        // Add error indicator
        _hasDetailLoadError: true,
        _errorMessage: error instanceof Error ? error.message : "Failed to load details",
        _isLoadingDetails: false
      };
      
      setSelectedResourceForDetail(fallbackResource);
      if (!isResourceModalOpen) {
        setIsResourceModalOpen(true);
      }
    } finally {
      // Clear loading state
      setLoadingResourceId(null);
    }
  }, [loadingResourceId, isResourceModalOpen]);

  // Update ref whenever function changes
  useEffect(() => {
    handleViewResourceDetailRef.current = handleViewResourceDetail;
  }, [handleViewResourceDetail]);

  const handleCloseResourceModal = useCallback(() => {
    setIsResourceModalOpen(false);
    setSelectedResourceForDetail(null);
  }, []);

  return (
    <div className="h-screen flex flex-col pb-5 bg-bgLinearGradient dark:bg-none dark:bg-darkBackground">
      {/* header */}
      <div className="h-10 px-5 py-4 flex justify-end items-center gap-[5px] mb-3">
        {isStartChat && (
          <button
            className="flex justify-center items-center w-8 h-8 p-[6px] rounded-lg hover:opacity-80 bg-primaryBlue"
            onClick={handleNewChat}
          >
            <img src={newChatIcon} alt="New chat" className="w-[20px] h-[20px] object-contain" />
          </button>
        )}
        {/* <a
          className="flex justify-center items-center w-8 h-8 p-[6px] rounded-lg bg-white"
          href="./agentConfig.html"
          title="Agent configuration"
        >
          <img src={settingsIcon} alt="settings" className="w-5 h-5 object-contain" />
        </a> */}
        {!isStartChat && (
          <button
            className="flex justify-center items-center w-8 h-8 p-[6px] rounded-lg bg-white dark:bg-primaryBlue hover:opacity-80 transition-opacity"
            onClick={handleOpenAboutModal}
          >
            <img src={infoIcon} alt="icon" className="w-5 h-5 object-contain dark:invert" />
          </button>
        )}
      </div>

      {/* chat container */}
      <div
        style={
          {
            // msOverflowStyle: "none",
            // scrollbarWidth: "none",
            // scrollBehavior: isInitialLoad ? "auto" : "smooth",
          }
        }
        className="w-full px-4 py-2 overflow-y-auto flex-1"
      >
        {!isStartChat ? (
          <>
            <p className="text-[20px] font-[600] leading-[28px] mx-10 mb-4 text-center text-darkGray dark:text-softGray">
              {greeting}
            </p>
            <div className="flex flex-col gap-4">
              {promptChat.map((item, index) => (
                <div
                  key={index}
                  style={{
                    boxShadow: "0 0 2px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.14)",
                  }}
                  className="bg-white px-3 py-4 rounded-lg cursor-pointer border-[1px] border-lightGray dark:border-softGray dark:bg-darkBackground"
                  onClick={() => handleChooseDefaultQuestion(item.description)}
                >
                  <div className="flex justify-start items-center h-[20p] gap-2 mb-3">
                    <div className="w-[20px] h-[20px]">
                      <img src={item.icon} alt="icon" className="w-full h-full object-contain" />
                    </div>
                    <p className="font-[600] text-[14px] leading-[20px] text-darkGray dark:text-softGray">
                      {item.title}
                    </p>
                  </div>
                  <p className="font-[400] text-[14px] leading-[20px] text-darkGray dark:text-softGray">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-center gap-2 mb-6">
              <p className="font-[600] text-[12px] leading-[16px] text-mediumGray dark:text-softGray">Today</p>
              <div className="flex-1 border-t-[1px] border-solid border-lightGray dark:border-softGray"></div>
            </div>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? "justify-end" : "justify-start"} group relative mb-6`}
                style={{ overflow: "visible" }}
              >
                {/* User Message */}
                {message.isUser ? (
                  <div
                    className="font-[400] text-[14px] leading-[20px] px-5 py-3 rounded-2xl relative bg-lightBlue text-darkGray dark:bg-darkBackground dark:text-softGray dark:border-[0.5px] dark:border-softGray"
                    onMouseEnter={() => setHoveredMessageId(message.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    <div>{handleRenderMessage(message)}</div>

                    {/* User Message Actions */}
                    {hoveredMessageId === message.id && (
                      <div
                        style={{
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)",
                        }}
                        className="absolute -top-3 -right-3 flex items-center gap-0 px-1 py-1 rounded-lg z-50 animate-in fade-in duration-200 bg-white border-[1px] border-lightGray text-darkGray"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditMessage(message.id, message.content);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors duration-150"
                          title="Edit message"
                        >
                          <Edit size={14} className="text-mediumGray" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyMessage(message.content);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors duration-150"
                          title="Copy message"
                        >
                          <Copy size={14} className="text-mediumGray" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookmarkMessage(message.id, message.content);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors duration-150"
                          title="Bookmark message"
                        >
                          <Bookmark size={14} className="text-mediumGray" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareMessage(message.id, message.content);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors duration-150"
                          title="Share message"
                        >
                          <Link size={14} className="text-mediumGray" strokeWidth={1.5} />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Agent Message */
                  <div className="flex items-start gap-3 w-full group">
                    {/* Avatar */}
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg
                        width={200}
                        height={200}
                        viewBox="0 0 200 200"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M179.999 104.505C179.999 109.549 176.244 113.803 171.239 114.428L148.038 117.329C143.398 118.254 140.001 122.338 140.001 127.134V161.1C140.001 166.144 136.245 170.398 131.241 171.023L71.2404 178.523C65.2719 179.269 60 174.615 60 168.6V114.999L99.9986 109.285L99.9967 43.3163C99.9966 38.1153 103.984 33.7825 109.167 33.3505L139.997 30.7812C139.997 35.5421 143.542 39.5578 148.266 40.1483L171.238 43.0198C176.242 43.6453 179.997 47.899 179.997 52.9421L179.999 104.505Z"
                          fill="url(#paint0_linear_497_17466)"
                        />
                        <path
                          d="M60 170.033V142.194C60 137.153 63.7511 132.901 68.7512 132.272L20 138.366V147.871C20 152.915 23.7554 157.169 28.7597 157.794L51.731 160.666C56.455 161.256 60 165.272 60 170.033Z"
                          fill="url(#paint1_linear_497_17466)"
                        />
                        <path
                          opacity={0.5}
                          d="M28.7598 90.5725C23.7555 91.1981 20.0001 95.4521 20.0001 100.495L20 155.16V152.321C20 147.278 23.7554 143.024 28.7597 142.399L91.2404 134.588C96.2447 133.963 100 129.709 100 124.666V81.6675L28.7598 90.5725Z"
                          fill="black"
                        />
                        <path
                          opacity={0.5}
                          d="M21.1445 90.8469C22.6242 88.0315 25.4126 85.9898 28.7598 85.5714L60 81.6664V81.6284L105 76.0034V124.666C105 132.23 99.367 138.611 91.8606 139.55L29.3798 147.36C27.299 147.62 22.186 149.131 20.5282 151.082C20.1862 150.074 20.0011 148.997 20 147.88L20.0001 100.325C20.0048 96.7387 20.1631 93.427 21.1445 90.8469Z"
                          fill="black"
                          fillOpacity={0.5}
                        />
                        <path
                          d="M28.7598 85.5725C23.7555 86.1981 20.0001 90.4521 20.0001 95.4953L20 150.16V147.321C20 142.278 23.7554 138.024 28.7596 137.399L91.2404 129.588C96.2447 128.963 100 124.709 100 119.666V76.6675L28.7598 85.5725Z"
                          fill="url(#paint2_linear_497_17466)"
                        />
                        <path
                          d="M28.7598 85.5725C23.7555 86.1981 20.0001 90.4521 20.0001 95.4953L20 150.16V147.321C20 142.278 23.7554 138.024 28.7596 137.399L91.2404 129.588C96.2447 128.963 100 124.709 100 119.666V76.6675L28.7598 85.5725Z"
                          fill="url(#paint3_linear_497_17466)"
                          fillOpacity={0.6}
                        />
                        <path
                          d="M100 113.828V85.4945C100 80.4541 103.751 76.202 108.751 75.5728L60 81.6667V91.1722C60 96.2154 63.7554 100.469 68.7597 101.095L91.2403 103.905C96.2446 104.531 100 108.785 100 113.828Z"
                          fill="url(#paint4_linear_497_17466)"
                        />
                        <path
                          opacity={0.5}
                          d="M68.7597 33.9605C63.7554 34.5861 60 38.8401 60 43.8833V96.6663V95.4941C60 90.4509 63.7554 86.1968 68.7597 85.5713L131.24 77.7612C136.245 77.1357 140 72.8817 140 67.8384V36.3833C140 30.3683 134.728 25.7145 128.76 26.4605L68.7597 33.9605Z"
                          fill="black"
                        />
                        <path
                          opacity={0.5}
                          d="M60 95.4864C60.0023 92.9667 66.8792 90.846 69.3798 90.5334L131.861 82.7233C139.367 81.785 145 75.404 145 67.8392V39.113C142.001 37.5141 139.999 34.3574 139.999 30.7801L139.982 30.7815C139.669 25.4713 135.236 21.3659 129.988 21.3819C129.583 21.3831 129.173 21.4089 128.76 21.4605L68.7597 28.9605C65.4123 29.3789 62.6238 31.4208 61.1442 34.2364C60.1475 36.8572 60 40.2327 60 43.884V95.4864Z"
                          fill="black"
                          fillOpacity={0.5}
                        />
                        <path
                          d="M68.7597 28.9615C63.7554 29.587 60 33.841 60 38.8843V91.6672V90.495C60 85.4518 63.7554 81.1978 68.7597 80.5722L131.24 72.7622C136.245 72.1366 140 67.8826 140 62.8394V31.3843C140 25.3693 134.728 20.7154 128.76 21.4615L68.7597 28.9615Z"
                          fill="url(#paint5_linear_497_17466)"
                        />
                        <path
                          d="M68.7597 28.9615C63.7554 29.587 60 33.841 60 38.8843V91.6672V90.495C60 85.4518 63.7554 81.1978 68.7597 80.5722L131.24 72.7622C136.245 72.1366 140 67.8826 140 62.8394V31.3843C140 25.3693 134.728 20.7154 128.76 21.4615L68.7597 28.9615Z"
                          fill="url(#paint6_linear_497_17466)"
                          fillOpacity={0.8}
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_497_17466"
                            x1={63.3333}
                            y1={185.187}
                            x2={246.32}
                            y2={99.2355}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#003580" />
                            <stop offset={0.299454} stopColor="#0057AD" />
                            <stop offset={1} stopColor="#16BFDF" />
                          </linearGradient>
                          <linearGradient
                            id="paint1_linear_497_17466"
                            x1={20}
                            y1={141.033}
                            x2={73.3333}
                            y2={141.033}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#0E637A" />
                            <stop offset={1} stopColor="#0074BD" />
                          </linearGradient>
                          <linearGradient
                            id="paint2_linear_497_17466"
                            x1={114.286}
                            y1={139.965}
                            x2={22.7091}
                            y2={93.9545}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#0986B3" />
                            <stop offset={0.721629} stopColor="#16BFDF" />
                            <stop offset={1} stopColor="#3DD9EB" />
                          </linearGradient>
                          <linearGradient
                            id="paint3_linear_497_17466"
                            x1={20}
                            y1={111.667}
                            x2={32.1429}
                            y2={111.667}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#0BA0C5" />
                            <stop offset={0.499692} stopColor="#0BA0C5" stopOpacity={0.263415} />
                            <stop offset={1} stopColor="#0BA0C5" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient
                            id="paint4_linear_497_17466"
                            x1={46.6667}
                            y1={101.31}
                            x2={100}
                            y2={101.31}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#117B97" />
                            <stop offset={1} stopColor="#1392B4" />
                          </linearGradient>
                          <linearGradient
                            id="paint5_linear_497_17466"
                            x1={65}
                            y1={100.957}
                            x2={136.608}
                            y2={27.9792}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#3DCBFF" />
                            <stop offset={0.524843} stopColor="#6EEDED" />
                            <stop offset={1} stopColor="#9BF3AF" />
                          </linearGradient>
                          <linearGradient
                            id="paint6_linear_497_17466"
                            x1={60}
                            y1={59.9998}
                            x2={72.8571}
                            y2={59.9998}
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#3DCBFF" />
                            <stop offset={0.433173} stopColor="#3DCBFF" stopOpacity={0.339056} />
                            <stop offset={1} stopColor="#3DCBFF" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>

                    {/* Message Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[14px] font-[600] text-darkGray dark:text-softGray">Resource Finder</span>
                        <div className="w-1 h-1 rounded-full bg-lightGray" />
                      </div>

                      {/* Content */}
                      <div className="text-[14px] leading-[20px] font-[400] text-darkGray dark:text-softGray">
                        {message.isTyping ? (
                          <div className="flex items-center space-x-2">
                            <Loader2 size={16} className="animate-spin" />
                          </div>
                        ) : (
                          <div>{handleRenderMessage(message)}</div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {!message.isTyping && (
                        <div className="flex items-center gap-0.5 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => handleRetryAgentMessage(message.id)}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-150 dark:hover:bg-darkBackground dark:hover:opacity-50"
                            title="Retry"
                          >
                            <RotateCcw size={14} className="text-mediumGray dark:text-softGray" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleCopyAgentMessage(message.content)}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-150 dark:hover:bg-darkBackground dark:hover:opacity-50"
                            title="Copy response"
                          >
                            <Copy size={14} className="text-mediumGray dark:text-softGray" strokeWidth={1.5} />
                          </button>
                          {/* <button
                            onClick={() => handleThumbsUp(message.id)}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-150 dark:hover:bg-darkBackground dark:hover:opacity-50"
                            title="Good response"
                          >
                            <ThumbsUp size={14} className="text-mediumGray dark:text-softGray" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleThumbsDown(message.id)}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-150 dark:hover:bg-darkBackground dark:hover:opacity-50"
                            title="Poor response"
                          >
                            <ThumbsDown size={14} className="text-mediumGray dark:text-softGray" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleSpeakMessage(typeof message.content === 'string' ? message.content : 'Response content')}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-150 dark:hover:bg-darkBackground dark:hover:opacity-50"
                            title="Read aloud"
                          >
                            <Volume2 size={14} className="text-mediumGray dark:text-softGray" strokeWidth={1.5} />
                          </button> */}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Scroll hint for interactive content */}
        {/* {showScrollHint && (
          <div className="flex justify-center py-2">
            <button
              onClick={scrollToBottom}
              className="px-3 py-1 rounded-full text-[12px] font-[500] hover:opacity-80 transition-opacity bg-lightBlue text-primaryBlue"
            >
              ↓ View more below
            </button>
          </div>
        )} */}

        <div ref={messagesEndRef} />
      </div>

      <AboutModal isOpen={isAboutModalOpen} onClose={handleCloseAboutModal} />
      
      {/* Resource Detail Modal */}
      <ResourceDetailPanel
        key={
          selectedResourceForDetail?.id ||
          selectedResourceForDetail?.ID ||
          selectedResourceForDetail?.email ||
          selectedResourceForDetail?.Email ||
          selectedResourceForDetail?.name ||
          selectedResourceForDetail?.Name ||
          "resource-panel"
        }
        resource={selectedResourceForDetail}
        isOpen={isResourceModalOpen}
        onClose={handleCloseResourceModal}
      />

      {/* Prompt Suggestions */}
      {/* {currentPromptSuggestions.length > 0 && (
        <div className="w-full mt-5 px-4 mb-3 overflow-x-auto">
          <div className="grid grid-rows-2 grid-flow-col auto-cols-max gap-x-3 gap-y-2 pb-2">
            {currentPromptSuggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isLoading}
                style={{
                  border: `1px solid ${colors.lightGray}`,
                  color: colors.darkGray,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
                className="text-left px-3 py-1.5 rounded-xl text-xs bg-white hover:bg-gray-50 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )} */}

      <div className={`w-full px-[10px] min-h-[92px] ${isLoading ? "opacity-50" : ""}`}>
        <div
          style={{
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            backdropFilter: "blur(40px)",
          }}
          className="bg-white w-full h-full p-3 rounded-[20px] border-[0.5px] border-lightGray dark:border-softGray dark:bg-darkBackground"
        >
          <textarea
            ref={inputRef}
            rows={1}
            disabled={isLoading}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
            className="px-2 py-[2px] mb-3 h-[24px] w-full outline-none resize-none font-[400] text-[14px] leading-5 dark:bg-darkBackground dark:text-softGray"
          />
          <div className="w-full h-8 flex justify-between items-center">
            <button className="flex justify-center items-center w-8 h-8 p-[6px] rounded-lg">
              <img src={addIcon} alt="Add" className="w-[20px] h-[20px] object-contain" />
            </button>
            <button
              className="flex justify-center items-center w-8 h-8 p-[6px] rounded-lg disabled:cursor-not-allowed disabled:opacity-50 bg-primaryBlue"
              onClick={() => handleSendMessage()}
              disabled={!canSendMessage}
            >
              <img src={arrowUpIcon} alt="Add" className="w-5 h-5 object-contain" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agent;
